// Shared dialog semantics for the imperative-DOM modals (BuildMenu, AddCompanyModal,
// DestroyConfirmModal, UserInfoModal, UserBuildingsPanel). Each file keeps creating and
// showing/hiding its own bespoke overlay+panel DOM; this only wires up role="dialog",
// aria-modal, aria-labelledby, a focus trap, focus restore, and one shared Escape stack
// (top of stack closes first) in place of each file's own document-level listener.
const escapeStack = [];
let escapeListenerInstalled = false;
function handleEscape(e) {
    if (e.key !== "Escape")
        return;
    const top = escapeStack[escapeStack.length - 1];
    top?.();
}
function ensureEscapeListener() {
    if (escapeListenerInstalled)
        return;
    document.addEventListener("keydown", handleEscape);
    escapeListenerInstalled = true;
}
function focusableElements(container) {
    return Array.from(container.querySelectorAll('a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])')).filter((el) => el.offsetParent !== null);
}
let nextLabelId = 0;
export function createModal(opts) {
    if (!opts.labelledBy.id) {
        opts.labelledBy.id = `modal-label-${nextLabelId++}`;
    }
    opts.panel.setAttribute("role", "dialog");
    opts.panel.setAttribute("aria-modal", "true");
    opts.panel.setAttribute("aria-labelledby", opts.labelledBy.id);
    if (!opts.panel.hasAttribute("tabindex"))
        opts.panel.tabIndex = -1;
    let isOpen = false;
    let previouslyFocused = null;
    let trapListener = null;
    let registeredClose = null;
    function trapFocus(e) {
        if (e.key !== "Tab")
            return;
        const focusable = focusableElements(opts.panel);
        if (focusable.length === 0) {
            e.preventDefault();
            return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;
        const outside = !(active instanceof Node) || !opts.panel.contains(active);
        if (e.shiftKey) {
            if (outside || active === first) {
                e.preventDefault();
                last.focus();
            }
        }
        else if (outside || active === last) {
            e.preventDefault();
            first.focus();
        }
    }
    function close() {
        if (!isOpen)
            return;
        isOpen = false;
        if (registeredClose) {
            const idx = escapeStack.lastIndexOf(registeredClose);
            if (idx !== -1)
                escapeStack.splice(idx, 1);
            registeredClose = null;
        }
        if (trapListener) {
            opts.panel.removeEventListener("keydown", trapListener);
            trapListener = null;
        }
        previouslyFocused?.focus();
        previouslyFocused = null;
    }
    function open(onRequestClose) {
        if (isOpen)
            return;
        isOpen = true;
        previouslyFocused =
            document.activeElement instanceof HTMLElement
                ? document.activeElement
                : null;
        ensureEscapeListener();
        registeredClose = onRequestClose;
        escapeStack.push(registeredClose);
        trapListener = trapFocus;
        opts.panel.addEventListener("keydown", trapListener);
        // Deferred one frame so the caller's own show() has already made the panel visible
        // (offsetParent-based focusableElements() would otherwise find nothing to focus).
        requestAnimationFrame(() => {
            const focusable = focusableElements(opts.panel);
            (focusable[0] ?? opts.panel).focus();
        });
    }
    return { open, close };
}
